import Processing from 'ext-ui/components/Processing/Processing'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  ChatBox,
  ChatMarkdown,
  ChatWrapper,
  InputBox,
  LoadingText,
  MessageBox,
  MessageWrapper,
  Timestamp,
} from 'src/agent/components/AgentChat/AgentChat.style'
import AgentChatField from 'src/agent/components/AgentChatField/AgentChatField'
import { useInsightsChat } from 'src/agent/components/InsightsChat/InsightsChat.queries'
import InsightsChatEmptyState from 'src/agent/components/InsightsChat/InsightsChatEmptyState'
import { useLoopingTypingEffect } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import {
  ConversationState,
  InterruptResolution,
} from 'src/queries/agent/network/agent.models'

export interface InsightsChatProps {
  chatKey?: string
}

const InsightsChat: React.FC<InsightsChatProps> = ({
  chatKey = 'insights',
}) => {
  const translate = useTranslate()

  const chatBoxRef = useRef<HTMLDivElement>(null)
  const loadingRef = useRef<HTMLDivElement>(null)
  const [userInput, setUserInput] = useState<string>('')

  const { isPending, messages, conversationState, startNewMessage } =
    useInsightsChat(chatKey)

  const lastMessage = useMemo(() => {
    if (messages.length === 0) return undefined
    return messages[messages.length - 1]
  }, [messages])

  const userPromptPlaceholder = useMemo(() => {
    const isInterrupt = lastMessage?.type === ConversationState.INTERRUPT
    const naturalFeedbackOption = lastMessage?.options?.find(
      (option) =>
        option.action === InterruptResolution.NATURAL_LANGUAGE_FEEDBACK,
    )
    const acceptsNaturalLanguageFeedback = !!naturalFeedbackOption
    const hasSuggestedPrompts =
      !!lastMessage?.suggestedPrompts && lastMessage.suggestedPrompts.length > 0

    if (isInterrupt && acceptsNaturalLanguageFeedback) {
      return naturalFeedbackOption.label
    } else if (isInterrupt && !acceptsNaturalLanguageFeedback) {
      return translate('Please select an action')
    } else if (hasSuggestedPrompts) {
      return translate('Or enter your request')
    }

    return translate('Ask about your spending patterns...')
  }, [lastMessage, translate])

  useEffect(() => {
    // Use requestAnimationFrame to ensure DOM has updated before scrolling
    const scrollToBottom = () => {
      if (chatBoxRef.current) {
        chatBoxRef.current.scrollTo({
          behavior: 'smooth',
          top: chatBoxRef.current.scrollHeight,
        })
      }
    }

    // Small delay to ensure content is rendered
    const timeoutId = setTimeout(scrollToBottom, 100)

    return () => clearTimeout(timeoutId)
  }, [messages, isPending])

  useEffect(() => {
    return () => {
      setUserInput('')
    }
  }, [])

  const loadingText = useLoopingTypingEffect([
    translate('Loading...'),
    translate('Working on it...'),
    translate('Just a moment...'),
    translate('Processing your request...'),
    translate('Thanks for your patience...'),
  ])

  const sendUserInput = (prompt: string): void => {
    if (prompt === '') return
    setUserInput('')
    switch (conversationState) {
      case ConversationState.INTERRUPT:
        startNewMessage({
          currentState: ConversationState.INTERRUPT,
          resolution: InterruptResolution.NATURAL_LANGUAGE_FEEDBACK,
          feedback: prompt,
        })
        break
      case ConversationState.MESSAGE:
        startNewMessage({
          currentState: ConversationState.MESSAGE,
          prompt,
        })
        break
      default:
        return conversationState
    }
  }

  const sendInterruptResolution = (
    resolution: InterruptResolution,
    feedback: string,
  ): void => {
    if (conversationState !== ConversationState.INTERRUPT) {
      // Silently ignore invalid state - should not happen in normal flow
      return
    }
    setUserInput('')
    startNewMessage({
      currentState: ConversationState.INTERRUPT,
      resolution,
      feedback,
    })
  }

  return (
    <ChatWrapper>
      <ChatBox ref={chatBoxRef}>
        {messages.length === 0 && !isPending && (
          <div className={'flex-column'}>
            <InsightsChatEmptyState onPromptClick={sendUserInput} />
          </div>
        )}
        {messages.map((msg, index) => {
          const messageKey = `${chatKey}-${index}-${msg.timestamp}-${msg.sender}`
          return (
            <div key={messageKey}>
              <MessageWrapper sender={msg.sender}>
                <Timestamp>{msg.timestamp}</Timestamp>
                <MessageBox sender={msg.sender}>
                  {msg.content && <ChatMarkdown>{msg.content}</ChatMarkdown>}
                  {msg.contentSrc && (
                    <ChatMarkdown>{translate(msg.contentSrc)}</ChatMarkdown>
                  )}
                </MessageBox>
              </MessageWrapper>
            </div>
          )
        })}
        {isPending && (
          <LoadingText ref={loadingRef}>
            <Processing /> {loadingText}
          </LoadingText>
        )}
      </ChatBox>
      <InputBox>
        <AgentChatField
          value={userInput}
          options={messages[messages.length - 1]?.options}
          onOptionClick={(action, prompt): void =>
            sendInterruptResolution(action, prompt)
          }
          suggestedPrompts={messages[messages.length - 1]?.suggestedPrompts}
          onSuggestedPromptClick={(promptTemplate): void => {
            const prompt =
              'displayPrompt' in promptTemplate
                ? promptTemplate.displayPrompt
                : undefined
            if (prompt) {
              sendUserInput(prompt)
            }
          }}
          onChange={(value): void => setUserInput(value)}
          onSubmit={(): void => sendUserInput(userInput)}
          placeholder={userPromptPlaceholder}
          disabled={isPending}
          loading={isPending}
        />
      </InputBox>
    </ChatWrapper>
  )
}

export default InsightsChat
